import cn from 'classnames';
import React from 'react';
import { useFeatureFlag } from 'src/feature-flags/consumers';
import { FoodItemMacros } from 'src/gqlReactTypings.generated.d';
import { Icon, IconColor } from 'web-common/src/shared/design-system/components/Icon';

export type NutritionMacrosProps = FoodItemMacros;

type MacroSection = {
  label: string;
  key: keyof Pick<NutritionMacrosProps, 'protein' | 'totalCarbohydrate' | 'totalFat' | 'sodium'>;
};

/** Mapping of display labels to macro keys, in display order */
const MACRO_SECTIONS: MacroSection[] = [
  { label: 'Protein', key: 'protein' },
  { label: 'Carbs', key: 'totalCarbohydrate' },
  { label: 'Fat', key: 'totalFat' },
  { label: 'Sodium', key: 'sodium' },
];

/** Mapping of sub-macro names to display labels */
const SUB_MACRO_LABELS: Record<string, string> = {
  totalSugars: 'Sugars',
  dietaryFiber: 'Fiber',
};

/** Format macro value with the appropriate unit */
function formatMacroValue(value: number, unit: string): string {
  return `${value}${unit}`;
}

export const NutritionMacros: React.FC<NutritionMacrosProps> = (props) => {
  const foodItemMacrosEnabled = useFeatureFlag('foodItemMacrosEnabled');
  const { calories, protein, totalCarbohydrate, totalFat, sodium } = props;

  /** Return null if feature flag is disabled or any required value is missing */
  const hasAllValues =
    calories !== undefined &&
    protein !== undefined &&
    totalCarbohydrate !== undefined &&
    totalFat !== undefined &&
    sodium !== undefined;

  if (!foodItemMacrosEnabled || !hasAllValues) {
    return null;
  }

  const renderDivider = <div className='my-2 h-0.5 bg-eggplant-100' />;

  const renderCalories = (
    <div className='py-2'>
      <div className='flex items-center justify-between text-body-600 text-eggplant-500'>
        <span>Calories</span>
        <span className='text-h5-bold'>{calories.value}</span>
      </div>
    </div>
  );

  const renderMacroList = MACRO_SECTIONS.map((section, index) => {
    const macro = props[section.key];
    const subMacros = macro.subMacros ?? [];
    const hasSubMacros = subMacros && subMacros.length > 0;

    return (
      <React.Fragment key={section.key}>
        <div className='py-2'>
          {/* Main macro item */}
          <div
            className={cn('flex items-center justify-between text-body-600 text-eggplant-500', {
              'mb-2': hasSubMacros,
            })}>
            <span>{section.label}</span>
            <span className='text-h5-bold'>{formatMacroValue(macro.value, macro.unit)}</span>
          </div>

          {/* Sub-macros (e.g., Sugars and Fiber under Carbs) */}
          {subMacros?.map((subMacro, subIndex) => {
            const displayLabel = SUB_MACRO_LABELS[subMacro.name] || subMacro.name;
            const isLastItem = subIndex >= (subMacros?.length ?? 0) - 1;
            return (
              <div key={subMacro.name} className={cn('flex items-center justify-between', { 'mb-1': !isLastItem })}>
                <span className='text-caption-400 text-eggplant-500'>{displayLabel}</span>
                <span className='text-body-600 text-eggplant-500'>
                  {formatMacroValue(subMacro.value.value, subMacro.value.unit)}
                </span>
              </div>
            );
          })}
        </div>

        {/* Divider (not after the last item) */}
        {index < MACRO_SECTIONS.length - 1 && renderDivider}
      </React.Fragment>
    );
  });

  const renderDisclaimer = (
    <div className='mt-3 flex items-start gap-1.5 rounded-xl bg-strawberry-50 px-2 py-3'>
      <Icon name='info' size={16} color={IconColor.STRAWBERRY_500} />
      <span className='text-caption-400 italic text-strawberry-500'>
        Nutrition information is estimated and may vary based on ingredients and preparation.
      </span>
    </div>
  );

  return (
    <div className='w-full bg-sourdough'>
      {/* Heading row */}
      <div className='mb-3 flex items-center justify-between'>
        <span className='text-caption-700-caps text-eggplant-500'>Nutrition</span>
        <span className='text-label-300 italic text-eggplant-500'>Per serving</span>
      </div>

      {/* Macros Container */}
      <div className='rounded-xl bg-white p-4'>
        {renderCalories}
        {renderDivider}
        {renderMacroList}
        {renderDisclaimer}
      </div>
    </div>
  );
};
